import { useEffect } from 'react';

/**
 * useReveal — IntersectionObserver hook that adds a class to every
 * `.revealSelector` element once it enters the viewport. Inside a
 * constructor-preview phone frame (elements have an `lp-*` ancestor)
 * the class is applied immediately to avoid flicker.
 *
 * Args:
 *   rootRef          — React ref to the root node
 *   dep              — any value to re-run the effect when it changes
 *   revealSelector   — selector to find animated elements (default '.reveal')
 *   inClass          — class added when visible (default 'in')
 *   previewAncestor  — selector identifying a preview wrapper (default '[class*="lp-"]')
 *   fallbackMs       — safety fallback that reveals everything after N ms (default 3000)
 */
export default function useReveal(rootRef, dep, {
  revealSelector = '.reveal',
  inClass        = 'in',
  previewAncestor = '[class*="lp-"]',
  fallbackMs     = 3000,
} = {}) {
  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return;
    const els = root.querySelectorAll(revealSelector);
    const inPreview = !!(previewAncestor && root.closest(previewAncestor));
    if (inPreview) {
      els.forEach(el => el.classList.add(inClass));
      return;
    }
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add(inClass); obs.unobserve(e.target); }
      }),
      { threshold: 0.05 }
    );
    els.forEach(el => obs.observe(el));
    const fallback = setTimeout(() => { els.forEach(el => el.classList.add(inClass)); }, fallbackMs);
    return () => { obs.disconnect(); clearTimeout(fallback); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootRef, dep, revealSelector, inClass, previewAncestor, fallbackMs]);
}
